<template>
<BaseTemplate app-name="RIS System" page-title="">
  <template #content>
    <div
      class="h-screen flex flex-col bg-gray-50 dark:bg-gray-900 text-gray-800 dark:text-gray-100 transition-colors duration-200"
    >
      <!-- Main Content -->
      <div class="flex-1 flex overflow-hidden">
        <!-- Content Area -->
        <div class="flex-1 overflow-hidden flex flex-col">
          <!-- Header -->
          <div class="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-4 shadow-sm">
            <div class="flex flex-wrap justify-between items-center gap-4">
              <div class="flex items-center gap-3">
                <h2 class="text-xl font-medium" :style="{ color: 'var(--clinic-font-color)' }">{{ currentView === 'list' ? 'Convênios Hospitalares' : 'Cadastro de Convênio' }}</h2>
                <button
                  v-if="currentView === 'form'"
                  class="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  @click="goToList"
                  aria-label="Voltar para lista"
                >
                  <ArrowLeftIcon class="w-5 h-5" />
                </button>
              </div>
              
              <!-- Indicador de Validação -->
              <div v-if="currentView === 'form'" class="flex items-center gap-3">
                <div class="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium"
                     :class="validationMessage.type === 'success' 
                       ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 border border-green-200 dark:border-green-700'
                       : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 border border-yellow-200 dark:border-yellow-700'">
                  <div v-if="validationMessage.type === 'success'" class="w-4 h-4 text-green-600 dark:text-green-400">
                    <CheckIcon class="w-4 h-4" />
                  </div>
                  <div v-else class="w-4 h-4 text-yellow-600 dark:text-yellow-400">
                    <AlertTriangleIcon class="w-4 h-4" />
                  </div>
                  <span>{{ missingFields.length === 0 ? 'Formulário Válido' : `${missingFields.length} campo(s) pendente(s)` }}</span>
                </div>
                
                <button
                  class="clinic-btn text-white rounded-md transition-colors shadow-sm flex items-center gap-2"
                  @click="saveInsurance"
                  :disabled="!isFormValid || isSaving"
                >
                  <SaveIcon class="w-4 h-4" />
                  <span>{{ isSaving ? 'Salvando...' : (editMode ? 'Atualizar' : 'Salvar') }} Convênio</span>
                </button>
              </div>
              
              <!-- Botão Novo Convênio (apenas na lista) -->
              <div v-if="currentView === 'list'" class="flex items-center gap-2">
                <button
                  class="clinic-btn text-white rounded-md transition-colors shadow-sm flex items-center gap-2"
                  @click="openNewInsuranceForm"
                >
                  <PlusIcon class="w-4 h-4" />
                  <span>Novo Convênio</span>
                </button>
              </div>
            </div>
            

          </div>

          <!-- Insurance List View -->
          <div v-if="currentView === 'list'" class="flex-1 overflow-y-auto p-4 bg-gray-50 dark:bg-gray-900">
            <div class="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-300 dark:border-gray-700 p-4">
              <!-- Search and Filter -->
              <div class="mb-4 flex flex-wrap gap-4 items-center">
                <div class="flex-1 min-w-[240px]">
                  <div class="relative">
                    <input
                      type="text"
                      v-model="searchQuery"
                      placeholder="Buscar convênios..."
                      @input="debouncedSearch"
                      class="h-10 w-full px-3 pl-10 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <SearchIcon class="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  </div>
                </div>

                <div class="relative">
                  <select
                    v-model="filterType"
                    @change="loadInsurances(1)"
                    class="h-10 px-3 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:ring-2 focus:ring-blue-500 appearance-none pr-8"
                  >
                    <option value="">Todos os tipos</option>
                    <option value="Particular">Particular</option>
                    <option value="Misto">Misto</option>
                    <option value="Seguro">Seguro</option>
                  </select>
                  <ChevronDownIcon class="w-4 h-4 text-gray-500 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>

                <button
                  @click="resetFilters"
                  class="h-10 px-3 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm hover:bg-gray-50 dark:hover:bg-gray-800 focus:ring-2 focus:ring-blue-500"
                >
                  <RefreshCwIcon class="w-4 h-4 inline-block" />
                </button>
              </div>

              <!-- Error Message -->
              <div v-if="errorMessage" class="mb-4 p-4 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-md">
                <p class="text-red-800 dark:text-red-400 text-sm">{{ errorMessage }}</p>
              </div>

              <!-- Loading Overlay -->
              <div v-if="isLoading" class="py-10 flex justify-center items-center">
                <LoaderIcon class="w-12 h-12 animate-spin text-gray-500 dark:text-gray-400" />
              </div>

              <!-- Insurance Table -->
              <div v-else class="overflow-x-auto">
                <table class="w-full border-collapse">
                  <thead>
                    <tr class="bg-gray-50 dark:bg-gray-900/50 text-left">
                      <th class="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                        Nome
                      </th>
                      <th class="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                        CNPJ
                      </th>
                      <th class="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                        Registro ANS
                      </th>
                      <th class="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                        Tipo
                      </th>
                      <th class="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                        Cidade/UF
                      </th>
                      <th class="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                        Status
                      </th>
                      <th class="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                        Convênio Faturamento
                      </th>
                      <th class="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="insurances.length === 0">
                      <td colspan="8" class="px-4 py-6 text-center text-gray-500 dark:text-gray-400">
                        Nenhum convênio encontrado
                      </td>
                    </tr>
                    <tr
                      v-for="insurance in insurances"
                      :key="insurance.id"
                      class="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                    >
                      <td class="px-4 py-3">
                        <div class="text-sm font-medium">{{ insurance.name }}</div>
                        <div class="text-xs text-gray-500 dark:text-gray-400">{{ insurance.contact_name }}</div>
                      </td>
                      <td class="px-4 py-3 text-sm font-mono">{{ formatCNPJDisplay(insurance.cnpj) }}</td>
                      <td class="px-4 py-3 text-sm">{{ insurance.ans_registry || '-' }}</td>
                      <td class="px-4 py-3 text-sm">{{ insurance.insurance_type }}</td>
                      <td class="px-4 py-3 text-sm">{{ insurance.city }}/{{ insurance.state }}</td>
                      <td class="px-4 py-3 text-sm">
                        <span
                          class="px-2 py-1 text-xs rounded-full"
                          :class="{
                            'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400': insurance.enabled,
                            'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400': !insurance.enabled
                          }"
                        >
                          {{ insurance.enabled ? 'Ativo' : 'Inativo' }}
                        </span>
                      </td>
                      <td class="px-4 py-3 text-sm">
                        <div v-if="insurance.convenio_faturamento_nome" class="text-green-600 dark:text-green-400">
                          {{ insurance.convenio_faturamento_nome }}
                        </div>
                        <div v-else class="text-gray-500 dark:text-gray-400">
                          Não vinculado
                        </div>
                      </td>
                      <td class="px-4 py-3 text-sm">
                        <div class="flex gap-2">
                          <button
                            @click="editInsurance(insurance)"
                            class="p-1 text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
                            title="Editar"
                          >
                            <EditIcon class="w-4 h-4" />
                          </button>
                          <button
                            v-if="!insurance.convenio_faturamento_id"
                            @click="openLinkConvenioModal(insurance)"
                            class="p-1 text-gray-500 hover:text-green-600 dark:text-gray-400 dark:hover:text-green-400"
                            title="Vincular Convênio"
                          >
                            <LinkIcon class="w-4 h-4" />
                          </button>
                          <button
                            @click="confirmDeleteInsurance(insurance)"
                            class="p-1 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                            title="Excluir"
                          >
                            <TrashIcon class="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>

                <!-- Pagination -->
                <div class="flex flex-col md:flex-row md:items-center md:justify-between my-4 gap-2">
                  <div class="flex items-center gap-2 justify-center">
                    <button
                      :disabled="currentPage === 1"
                      @click="loadInsurances(currentPage - 1)"
                      class="px-3 py-1 rounded bg-emerald-500 dark:bg-gray-700 text-gray-200 dark:text-gray-200 disabled:opacity-50"
                    >
                      Anterior
                    </button>
                    <span class="text-sm text-gray-700 dark:text-gray-200">
                      Página {{ currentPage }} de {{ totalPages }}
                    </span>
                    <button
                      :disabled="currentPage === totalPages || totalPages === 0"
                      @click="loadInsurances(currentPage + 1)"
                      class="px-3 py-1 rounded bg-emerald-500 dark:bg-gray-700 text-gray-200 dark:text-gray-200 disabled:opacity-50"
                    >
                      Próxima
                    </button>
                  </div>
                  <div class="flex items-center gap-2 justify-center">
                    <label for="page-size" class="text-sm text-gray-700 dark:text-gray-200">Itens por página:</label>
                    <select
                      v-model.number="pageSize"
                      @change="loadInsurances(1)"
                      class="rounded border px-2 py-1 text-sm dark:bg-gray-700 dark:text-white"
                    >
                      <option :value="5">5</option>
                      <option :value="10">10</option>
                      <option :value="20">20</option>
                      <option :value="50">50</option>
                    </select>
                    <span class="text-xs text-gray-500 dark:text-gray-400">Total: {{ totalCount }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Insurance Form View with Tabs -->
          <div v-if="currentView === 'form'" class="flex-1 overflow-y-auto p-4 bg-gray-50 dark:bg-gray-900">
            <div class="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-300 dark:border-gray-700">
              <!-- Tabs Navigation -->
              <div class="border-b border-gray-200 dark:border-gray-700">
                <nav class="flex space-x-8">
                  <button
                    v-for="tab in tabs"
                    :key="tab.id"
                    @click="activeTab = tab.id"
                    class="py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 flex items-center gap-2"
                    :class="{
                      'border-b-2': activeTab === tab.id,
                      'border-b-2': activeTab === tab.id
                    }"
                    :style="{
                      color: activeTab === tab.id ? 'var(--clinic-icon-color)' : 'var(--clinic-font-color)',
                      borderColor: activeTab === tab.id ? 'var(--clinic-icon-color)' : 'transparent'
                    }"
                  >
                    <component :is="tab.icon" class="w-4 h-4" />
                    {{ tab.name }}
                    
                    <!-- Indicador de campos pendentes na aba -->
                    <div v-if="getTabMissingFields(tab.id).length > 0" 
                         class="w-5 h-5 bg-yellow-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                      {{ getTabMissingFields(tab.id).length }}
                    </div>
                  </button>
                </nav>
              </div>

              <!-- Tab Content -->
              <div class="p-6">
                <!-- ABA: Geral -->
                <div v-if="activeTab === 'general'" class="space-y-6">
                  <!-- Dados Obrigatórios -->
                  <div>
                    <h3 class="text-lg font-medium mb-4 text-gray-900 dark:text-white">Dados Obrigatórios (*)</h3>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div class="md:col-span-2">
                        <label for="name" class="block text-sm font-medium mb-1">Nome *</label>
                        <div class="relative">
                          <input
                            id="name"
                            type="text"
                            v-model="currentInsurance.name"
                            required
                            :class="[
                              'w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                              !currentInsurance.name || currentInsurance.name.trim() === '' ? 'border-red-300 dark:border-red-600' : 'border-gray-200 dark:border-gray-700'
                            ]"
                          />
                          <div v-if="!currentInsurance.name || currentInsurance.name.trim() === ''" 
                               class="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 dark:text-red-400"
                               title="Campo obrigatório">
                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                            </svg>
                          </div>
                        </div>
                      </div>
                      
                      <div>
                        <label for="cnpj" class="block text-sm font-medium mb-1">CNPJ Convênio *</label>
                        <div class="relative">
                          <input
                            id="cnpj"
                            type="text"
                            v-model="currentInsurance.cnpj"
                            required
                            placeholder="00.000.000/0000-00"
                            maxlength="18"
                            @input="formatCNPJInput"
                            @blur="validateCNPJField"
                            :class="[
                              'w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                              cnpjError ? 'border-red-300 dark:border-red-600' : 
                              (!currentInsurance.cnpj || currentInsurance.cnpj.trim() === '') ? 'border-red-300 dark:border-red-600' : 
                              'border-gray-200 dark:border-gray-700'
                            ]"
                          />
                          <div v-if="!currentInsurance.cnpj || currentInsurance.cnpj.trim() === '' || cnpjError" 
                               class="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 dark:text-red-400"
                               title="Campo obrigatório">
                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                            </svg>
                          </div>
                        </div>
                        <p v-if="cnpjError" class="mt-1 text-sm text-red-600 dark:text-red-400">
                          {{ cnpjError }}
                        </p>
                        <p v-else-if="currentInsurance.cnpj && currentInsurance.cnpj.trim() !== '' && !cnpjError" class="mt-1 text-sm text-green-600 dark:text-green-400">
                          ✓ CNPJ válido
                        </p>
                      </div>
                      
                      <div>
                        <label for="ans_registry" class="block text-sm font-medium mb-1">Registro ANS *</label>
                        <div class="relative">
                          <input
                            id="ans_registry"
                            type="text"
                            v-model="currentInsurance.ans_registry"
                            required
                            maxlength="6"
                            @input="formatANSInput"
                            @blur="validateANSField"
                            :class="[
                              'w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                              ansError ? 'border-red-300 dark:border-red-600' : 
                              (!currentInsurance.ans_registry || currentInsurance.ans_registry.trim() === '') ? 'border-red-300 dark:border-red-600' : 
                              'border-gray-200 dark:border-gray-700'
                            ]"
                          />
                          <div v-if="!currentInsurance.ans_registry || currentInsurance.ans_registry.trim() === '' || ansError" 
                               class="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 dark:text-red-400"
                               title="Campo obrigatório">
                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                            </svg>
                          </div>
                        </div>
                        <p v-if="ansError" class="mt-1 text-sm text-red-600 dark:text-red-400">
                          {{ ansError }}
                        </p>
                        <p v-else-if="currentInsurance.ans_registry && currentInsurance.ans_registry.trim() !== '' && !ansError" class="mt-1 text-sm text-green-600 dark:text-green-400">
                          ✓ Registro ANS válido
                        </p>
                      </div>
                      
                      <div>
                        <label for="insurance_type" class="block text-sm font-medium mb-1">Tipo de Convênio *</label>
                        <div class="relative">
                          <select
                            id="insurance_type"
                            v-model="currentInsurance.insurance_type"
                            required
                            :class="[
                              'w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none pr-8',
                              !currentInsurance.insurance_type || currentInsurance.insurance_type.trim() === '' ? 'border-red-300 dark:border-red-600' : 'border-gray-200 dark:border-gray-700'
                            ]"
                          >
                            <option value="">Selecione</option>
                            <option value="Particular">Particular</option>
                            <option value="Misto">Misto</option>
                            <option value="Seguro">Seguro</option>
                          </select>
                          <div v-if="!currentInsurance.insurance_type || currentInsurance.insurance_type.trim() === ''" 
                               class="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 dark:text-red-400 pointer-events-none"
                               title="Campo obrigatório">
                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                            </svg>
                          </div>
                        </div>
                      </div>
                      
                      <div>
                        <label for="zip_code" class="block text-sm font-medium mb-1">CEP *</label>
                        <div class="relative">
                          <input
                            id="zip_code"
                            type="text"
                            v-model="currentInsurance.zip_code"
                            required
                            placeholder="00000-000"
                            maxlength="9"
                            @input="formatCEPInput"
                            @blur="validateCEPField"
                            :class="[
                              'w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                              cepValidationError ? 'border-red-300 dark:border-red-600' : 
                              (!currentInsurance.zip_code || currentInsurance.zip_code.trim() === '') ? 'border-red-300 dark:border-red-600' : 
                              'border-gray-200 dark:border-gray-700'
                            ]"
                          />
                          <div v-if="!currentInsurance.zip_code || currentInsurance.zip_code.trim() === '' || cepValidationError" 
                               class="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 dark:text-red-400"
                               title="Campo obrigatório">
                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                            </svg>
                          </div>
                        </div>
                        <p v-if="cepValidationError" class="mt-1 text-sm text-red-600 dark:text-red-400">
                          {{ cepValidationError }}
                        </p>
                        <p v-else-if="currentInsurance.zip_code && currentInsurance.zip_code.trim() !== '' && !cepValidationError" class="mt-1 text-sm text-green-600 dark:text-green-400">
                          ✓ CEP válido
                        </p>
                      </div>
                      
                      <div>
                        <label for="state" class="block text-sm font-medium mb-1">UF *</label>
                        <div class="relative">
                          <select
                            id="state"
                            v-model="currentInsurance.state"
                            required
                            :class="[
                              'w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none pr-8',
                              !currentInsurance.state || currentInsurance.state.trim() === '' ? 'border-red-300 dark:border-red-600' : 'border-gray-200 dark:border-gray-700'
                            ]"
                          >
                            <option value="">Selecione</option>
                            <option value="SP">SP</option>
                            <option value="RJ">RJ</option>
                            <option value="MG">MG</option>
                            <option value="RS">RS</option>
                            <option value="PR">PR</option>
                            <option value="SC">SC</option>
                            <option value="BA">BA</option>
                            <option value="GO">GO</option>
                            <option value="PE">PE</option>
                            <option value="CE">CE</option>
                            <option value="PA">PA</option>
                            <option value="MT">MT</option>
                            <option value="MS">MS</option>
                            <option value="ES">ES</option>
                            <option value="PB">PB</option>
                            <option value="RN">RN</option>
                            <option value="PI">PI</option>
                            <option value="AL">AL</option>
                            <option value="SE">SE</option>
                            <option value="TO">TO</option>
                            <option value="MA">MA</option>
                            <option value="RO">RO</option>
                            <option value="RR">RR</option>
                            <option value="AP">AP</option>
                            <option value="AC">AC</option>
                            <option value="AM">AM</option>
                            <option value="DF">DF</option>
                          </select>
                          <div v-if="!currentInsurance.state || currentInsurance.state.trim() === ''" 
                               class="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 dark:text-red-400 pointer-events-none"
                               title="Campo obrigatório">
                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                            </svg>
                          </div>
                        </div>
                      </div>
                      
                      <div>
                        <label for="city" class="block text-sm font-medium mb-1">Cidade *</label>
                        <div class="relative">
                          <input
                            id="city"
                            type="text"
                            v-model="currentInsurance.city"
                            required
                            :class="[
                              'w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                              !currentInsurance.city || currentInsurance.city.trim() === '' ? 'border-red-300 dark:border-red-600' : 'border-gray-200 dark:border-gray-700'
                            ]"
                          />
                          <div v-if="!currentInsurance.city || currentInsurance.city.trim() === ''" 
                               class="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 dark:text-red-400"
                               title="Campo obrigatório">
                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                            </svg>
                          </div>
                        </div>
                      </div>
                      
                      <div>
                        <label for="street_type" class="block text-sm font-medium mb-1">Tipo Logradouro *</label>
                        <div class="relative">
                          <select
                            id="street_type"
                            v-model="currentInsurance.street_type"
                            required
                            :class="[
                              'w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none pr-8',
                              !currentInsurance.street_type || currentInsurance.street_type.trim() === '' ? 'border-red-300 dark:border-red-600' : 'border-gray-200 dark:border-gray-700'
                            ]"
                          >
                            <option value="">Selecione</option>
                            <option value="Rua">Rua</option>
                            <option value="Avenida">Avenida</option>
                            <option value="Alameda">Alameda</option>
                            <option value="Praça">Praça</option>
                            <option value="Travessa">Travessa</option>
                            <option value="Vila">Vila</option>
                            <option value="Estrada">Estrada</option>
                            <option value="Rodovia">Rodovia</option>
                            <option value="Boulevard">Boulevard</option>
                            <option value="Largo">Largo</option>
                          </select>
                          <div v-if="!currentInsurance.street_type || currentInsurance.street_type.trim() === ''" 
                               class="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 dark:text-red-400 pointer-events-none"
                               title="Campo obrigatório">
                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                            </svg>
                          </div>
                        </div>
                      </div>
                      
                      <div>
                        <label for="street" class="block text-sm font-medium mb-1">Logradouro *</label>
                        <div class="relative">
                          <input
                            id="street"
                            type="text"
                            v-model="currentInsurance.street"
                            required
                            :class="[
                              'w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                              !currentInsurance.street || currentInsurance.street.trim() === '' ? 'border-red-300 dark:border-red-600' : 'border-gray-200 dark:border-gray-700'
                            ]"
                          />
                          <div v-if="!currentInsurance.street || currentInsurance.street.trim() === ''" 
                               class="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 dark:text-red-400"
                               title="Campo obrigatório">
                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                            </svg>
                          </div>
                        </div>
                      </div>
                      
                      <div>
                        <label for="number" class="block text-sm font-medium mb-1">Número *</label>
                        <div class="relative">
                          <input
                            id="number"
                            type="text"
                            v-model="currentInsurance.number"
                            required
                            :class="[
                              'w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                              !currentInsurance.number || currentInsurance.number.trim() === '' ? 'border-red-300 dark:border-red-600' : 'border-gray-200 dark:border-gray-700'
                            ]"
                          />
                          <div v-if="!currentInsurance.number || currentInsurance.number.trim() === ''" 
                               class="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 dark:text-red-400"
                               title="Campo obrigatório">
                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                            </svg>
                          </div>
                        </div>
                      </div>
                      
                      <div>
                        <label for="email1" class="block text-sm font-medium mb-1">E-mail 1 *</label>
                        <div class="relative">
                          <input
                            id="email1"
                            type="email"
                            v-model="currentInsurance.email1"
                            required
                            @blur="validateEmailField"
                            :class="[
                              'w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                              emailError ? 'border-red-300 dark:border-red-600' : 
                              (!currentInsurance.email1 || currentInsurance.email1.trim() === '') ? 'border-red-300 dark:border-red-600' : 
                              'border-gray-200 dark:border-gray-700'
                            ]"
                          />
                          <div v-if="!currentInsurance.email1 || currentInsurance.email1.trim() === '' || emailError" 
                               class="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 dark:text-red-400"
                               title="Campo obrigatório">
                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                            </svg>
                          </div>
                        </div>
                        <p v-if="emailError" class="mt-1 text-sm text-red-600 dark:text-red-400">
                          {{ emailError }}
                        </p>
                        <p v-else-if="currentInsurance.email1 && currentInsurance.email1.trim() !== '' && !emailError" class="mt-1 text-sm text-green-600 dark:text-green-400">
                          ✓ E-mail válido
                        </p>
                      </div>
                      
                      <div>
                        <label for="contact_name" class="block text-sm font-medium mb-1">Nome do Contato *</label>
                        <div class="relative">
                          <input
                            id="contact_name"
                            type="text"
                            v-model="currentInsurance.contact_name"
                            required
                            :class="[
                              'w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                              !currentInsurance.contact_name || currentInsurance.contact_name.trim() === '' ? 'border-red-300 dark:border-red-600' : 'border-gray-200 dark:border-gray-700'
                            ]"
                          />
                          <div v-if="!currentInsurance.contact_name || currentInsurance.contact_name.trim() === ''" 
                               class="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 dark:text-red-400"
                               title="Campo obrigatório">
                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                            </svg>
                          </div>
                        </div>
                      </div>
                      
                      <div>
                        <label for="phone1" class="block text-sm font-medium mb-1">Telefone 1 *</label>
                        <div class="relative">
                          <input
                            id="phone1"
                            type="tel"
                            v-model="currentInsurance.phone1"
                            required
                            placeholder="(00) 0000-0000"
                            maxlength="15"
                            @input="formatPhoneInput"
                            @blur="validatePhoneField"
                            :class="[
                              'w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                              phoneError ? 'border-red-300 dark:border-red-600' : 
                              (!currentInsurance.phone1 || currentInsurance.phone1.trim() === '') ? 'border-red-300 dark:border-red-600' : 
                              'border-gray-200 dark:border-gray-700'
                            ]"
                          />
                          <div v-if="!currentInsurance.phone1 || currentInsurance.phone1.trim() === '' || phoneError" 
                               class="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 dark:text-red-400"
                               title="Campo obrigatório">
                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                            </svg>
                          </div>
                        </div>
                        <p v-if="phoneError" class="mt-1 text-sm text-red-600 dark:text-red-400">
                          {{ phoneError }}
                        </p>
                        <p v-else-if="currentInsurance.phone1 && currentInsurance.phone1.trim() !== '' && !phoneError" class="mt-1 text-sm text-green-600 dark:text-green-400">
                          ✓ Telefone válido
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <!-- Mensagem de ajuda -->
                  <div class="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg">
                    <div class="flex items-start gap-2">
                      <div class="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0">
                        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                        </svg>
                      </div>
                      <div class="flex-1">
                        <h4 class="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">
                          Campos obrigatórios desta aba:
                        </h4>
                        <div class="text-sm text-blue-700 dark:text-blue-300">
                          Nome, CNPJ, Registro ANS, Tipo de Convênio, CEP, UF, Cidade, Tipo de Logradouro, Logradouro, Número, E-mail 1, Nome do Contato e Telefone 1.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- ABA: Validações -->
                <div v-if="activeTab === 'validations'" class="space-y-6">
                  <!-- Validações Obrigatórias -->
                  <div>
                    <h3 class="text-lg font-medium mb-4 text-gray-900 dark:text-white">Validações Obrigatórias (TISS/RIS)</h3>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label for="card_validation" class="block text-sm font-medium mb-1">Validação de Carteira*</label>
                        <div class="relative">
                          <select
                            id="card_validation"
                            v-model="currentInsurance.card_validation"
                            required
                            :class="[
                              'w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none pr-8',
                              !currentInsurance.card_validation || currentInsurance.card_validation.trim() === '' ? 'border-red-300 dark:border-red-600' : 'border-gray-200 dark:border-gray-700'
                            ]"
                          >
                            <option value="">Selecione</option>
                            <option value="Sim">Sim</option>
                            <option value="Não">Não</option>
                          </select>
                          <div v-if="!currentInsurance.card_validation || currentInsurance.card_validation.trim() === ''" 
                               class="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 dark:text-red-400 pointer-events-none"
                               title="Campo obrigatório">
                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                            </svg>
                          </div>
                        </div>
                      </div>
                      
                      <div>
                        <label for="provider_guide" class="block text-sm font-medium mb-1">Guia Prestador/Operadora*</label>
                        <div class="relative">
                          <select
                            id="provider_guide"
                            v-model="currentInsurance.provider_guide"
                            required
                            :class="[
                              'w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none pr-8',
                              !currentInsurance.provider_guide || currentInsurance.provider_guide.trim() === '' ? 'border-red-300 dark:border-red-600' : 'border-gray-200 dark:border-gray-700'
                            ]"
                          >
                            <option value="">Selecione</option>
                            <option value="Sim">Sim</option>
                            <option value="Não">Não</option>
                          </select>
                          <div v-if="!currentInsurance.provider_guide || currentInsurance.provider_guide.trim() === ''" 
                               class="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 dark:text-red-400 pointer-events-none"
                               title="Campo obrigatório">
                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                            </svg>
                          </div>
                        </div>
                      </div>
                      
                      <div>
                        <label for="guide_date" class="block text-sm font-medium mb-1">Data Guia*</label>
                        <div class="relative">
                          <select
                            id="guide_date"
                            v-model="currentInsurance.guide_date"
                            required
                            :class="[
                              'w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none pr-8',
                              !currentInsurance.guide_date || currentInsurance.guide_date.trim() === '' ? 'border-red-300 dark:border-red-600' : 'border-gray-200 dark:border-gray-700'
                            ]"
                          >
                            <option value="">Selecione</option>
                            <option value="Sim">Sim</option>
                            <option value="Não">Não</option>
                          </select>
                          <div v-if="!currentInsurance.guide_date || currentInsurance.guide_date.trim() === ''" 
                               class="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 dark:text-red-400 pointer-events-none"
                               title="Campo obrigatório">
                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                            </svg>
                          </div>
                        </div>
                      </div>
                      
                      <div>
                        <label for="request_date_time" class="block text-sm font-medium mb-1">Data/Hora Solicitação*</label>
                        <div class="relative">
                          <select
                            id="request_date_time"
                            v-model="currentInsurance.request_date_time"
                            required
                            :class="[
                              'w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none pr-8',
                              !currentInsurance.request_date_time || currentInsurance.request_date_time.trim() === '' ? 'border-red-300 dark:border-red-600' : 'border-gray-200 dark:border-gray-700'
                            ]"
                          >
                            <option value="">Selecione</option>
                            <option value="Sim">Sim</option>
                            <option value="Não">Não</option>
                          </select>
                          <div v-if="!currentInsurance.request_date_time || currentInsurance.request_date_time.trim() === ''" 
                               class="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 dark:text-red-400 pointer-events-none"
                               title="Campo obrigatório">
                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                            </svg>
                          </div>
                        </div>
                      </div>
                      
                      <div>
                        <label for="origin" class="block text-sm font-medium mb-1">Procedência*</label>
                        <div class="relative">
                          <select
                            id="origin"
                            v-model="currentInsurance.origin"
                            required
                            :class="[
                              'w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none pr-8',
                              !currentInsurance.origin || currentInsurance.origin.trim() === '' ? 'border-red-300 dark:border-red-600' : 'border-gray-200 dark:border-gray-700'
                            ]"
                          >
                            <option value="">Selecione</option>
                            <option value="Sim">Sim</option>
                            <option value="Não">Não</option>
                          </select>
                          <div v-if="!currentInsurance.origin || currentInsurance.origin.trim() === ''" 
                               class="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 dark:text-red-400 pointer-events-none"
                               title="Campo obrigatório">
                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                            </svg>
                          </div>
                        </div>
                      </div>
                      
                      <div>
                        <label for="cid" class="block text-sm font-medium mb-1">CID*</label>
                        <div class="relative">
                          <select
                            id="cid"
                            v-model="currentInsurance.cid"
                            required
                            :class="[
                              'w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none pr-8',
                              !currentInsurance.cid || currentInsurance.cid.trim() === '' ? 'border-red-300 dark:border-red-600' : 'border-gray-200 dark:border-gray-700'
                            ]"
                          >
                            <option value="">Selecione</option>
                            <option value="Sim">Sim</option>
                            <option value="Não">Não</option>
                          </select>
                          <div v-if="!currentInsurance.cid || currentInsurance.cid.trim() === ''" 
                               class="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 dark:text-red-400 pointer-events-none"
                               title="Campo obrigatório">
                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                            </svg>
                          </div>
                        </div>
                      </div>
                      
                      <div>
                        <label for="care_character" class="block text-sm font-medium mb-1">Caráter Atendimento*</label>
                        <div class="relative">
                          <select
                            id="care_character"
                            v-model="currentInsurance.care_character"
                            required
                            :class="[
                              'w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none pr-8',
                              !currentInsurance.care_character || currentInsurance.care_character.trim() === '' ? 'border-red-300 dark:border-red-600' : 'border-gray-200 dark:border-gray-700'
                            ]"
                          >
                            <option value="">Selecione</option>
                            <option value="Sim">Sim</option>
                            <option value="Não">Não</option>
                          </select>
                          <div v-if="!currentInsurance.care_character || currentInsurance.care_character.trim() === ''" 
                               class="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 dark:text-red-400 pointer-events-none"
                               title="Campo obrigatório">
                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                            </svg>
                          </div>
                        </div>
                      </div>
                      
                      <div>
                        <label for="cns_patient" class="block text-sm font-medium mb-1">CNS Paciente*</label>
                        <div class="relative">
                          <select
                            id="cns_patient"
                            v-model="currentInsurance.cns_patient"
                            required
                            :class="[
                              'w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none pr-8',
                              !currentInsurance.cns_patient || currentInsurance.cns_patient.trim() === '' ? 'border-red-300 dark:border-red-600' : 'border-gray-200 dark:border-gray-700'
                            ]"
                          >
                            <option value="">Selecione</option>
                            <option value="Sim">Sim</option>
                            <option value="Não">Não</option>
                          </select>
                          <div v-if="!currentInsurance.cns_patient || currentInsurance.cns_patient.trim() === ''" 
                               class="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 dark:text-red-400 pointer-events-none"
                               title="Campo obrigatório">
                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                            </svg>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Validações Opcionais -->
                  <div>
                    <h3 class="text-lg font-medium mb-4 text-gray-900 dark:text-white">Validações Opcionais</h3>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label for="registration_definitions" class="block text-sm font-medium mb-1">Definições de Matrícula</label>
                        <input
                          id="registration_definitions"
                          type="text"
                          v-model="currentInsurance.registration_definitions"
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      
                      <div>
                        <label for="doctor_code" class="block text-sm font-medium mb-1">Código do Médico</label>
                        <input
                          id="doctor_code"
                          type="text"
                          v-model="currentInsurance.doctor_code"
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      
                      <div>
                        <label for="validation_code" class="block text-sm font-medium mb-1">Código de Validação</label>
                        <input
                          id="validation_code"
                          type="text"
                          v-model="currentInsurance.validation_code"
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      
                      <div>
                        <label for="race" class="block text-sm font-medium mb-1">Raça</label>
                        <select
                          id="race"
                          v-model="currentInsurance.race"
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none pr-8"
                        >
                          <option value="">Selecione</option>
                          <option value="Branca">Branca</option>
                          <option value="Preta">Preta</option>
                          <option value="Parda">Parda</option>
                          <option value="Amarela">Amarela</option>
                          <option value="Indígena">Indígena</option>
                        </select>
                      </div>
                    </div>
                    
                    <div class="mt-4 space-y-3">
                      <div class="flex items-center">
                        <input
                          id="guide_logo"
                          type="checkbox"
                          v-model="currentInsurance.guide_logo"
                          class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label for="guide_logo" class="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                          Logomarca da guia
                        </label>
                      </div>
                      
                      <div class="flex items-center">
                        <input
                          id="guide_numbering"
                          type="checkbox"
                          v-model="currentInsurance.guide_numbering"
                          class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label for="guide_numbering" class="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                          Numeração de guias
                        </label>
                      </div>
                      
                      <div class="flex items-center">
                        <input
                          id="scheduling_limit"
                          type="checkbox"
                          v-model="currentInsurance.scheduling_limit"
                          class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label for="scheduling_limit" class="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                          Limite agendamento
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- ABA: Faturamento -->
                <div v-if="activeTab === 'billing'" class="space-y-6">
                  <!-- Configurações de Faturamento -->
                  <div>
                    <h3 class="text-lg font-medium mb-4 text-gray-900 dark:text-white">Configurações de Faturamento</h3>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label for="billing_type" class="block text-sm font-medium mb-1">Tipo de Convênio*</label>
                        <select
                          id="billing_type"
                          v-model="currentInsurance.billing_type"
                          required
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none pr-8"
                        >
                          <option value="">Selecione</option>
                          <option value="Particular">Particular</option>
                          <option value="Misto">Misto</option>
                          <option value="Seguro">Seguro</option>
                        </select>
                      </div>
                      
                      <div>
                        <label for="square_meter_value" class="block text-sm font-medium mb-1">Valor do M²*</label>
                        <input
                          id="square_meter_value"
                          type="number"
                          step="0.01"
                          v-model="currentInsurance.square_meter_value"
                          required
                          :class="[
                            'w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:border-transparent',
                            formErrors.square_meter_value 
                              ? 'border-red-500 focus:ring-red-500' 
                              : 'border-gray-200 dark:border-gray-700 focus:ring-blue-500'
                          ]"
                        />
                        <p v-if="formErrors.square_meter_value" class="mt-1 text-sm text-red-600 dark:text-red-400">
                          {{ formErrors.square_meter_value }}
                        </p>
                      </div>
                      
                      <div>
                        <label for="default_company" class="block text-sm font-medium mb-1">Empresa Padrão*</label>
                        <input
                          id="default_company"
                          type="text"
                          v-model="currentInsurance.default_company"
                          required
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      
                      <div>
                        <label for="invoice_format" class="block text-sm font-medium mb-1">Formato da Fatura*</label>
                        <select
                          id="invoice_format"
                          v-model="currentInsurance.invoice_format"
                          required
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none pr-8"
                        >
                          <option value="">Selecione</option>
                          <option value="PDF">PDF</option>
                          <option value="XML">XML</option>
                          <option value="TXT">TXT</option>
                        </select>
                      </div>
                      
                      <div>
                        <label for="delivery_date" class="block text-sm font-medium mb-1">Data de Entrega*</label>
                        <input
                          id="delivery_date"
                          type="number"
                          min="1"
                          max="31"
                          v-model="currentInsurance.delivery_date"
                          required
                          placeholder="Dia do mês"
                          :class="[
                            'w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:border-transparent',
                            formErrors.delivery_date 
                              ? 'border-red-500 focus:ring-red-500' 
                              : 'border-gray-200 dark:border-gray-700 focus:ring-blue-500'
                          ]"
                        />
                        <p v-if="formErrors.delivery_date" class="mt-1 text-sm text-red-600 dark:text-red-400">
                          {{ formErrors.delivery_date }}
                        </p>
                      </div>
                      
                      <div>
                        <label for="bank_account" class="block text-sm font-medium mb-1">Conta Bancária*</label>
                        <input
                          id="bank_account"
                          type="text"
                          v-model="currentInsurance.bank_account"
                          required
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      
                      <div>
                        <label for="due_type" class="block text-sm font-medium mb-1">Tipo Vencimento*</label>
                        <select
                          id="due_type"
                          v-model="currentInsurance.due_type"
                          required
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none pr-8"
                        >
                          <option value="">Selecione</option>
                          <option value="Fixo">Fixo</option>
                          <option value="Variável">Variável</option>
                        </select>
                      </div>
                      
                      <div>
                        <label for="due_day" class="block text-sm font-medium mb-1">Dia do Vencimento*</label>
                        <input
                          id="due_day"
                          type="number"
                          min="1"
                          max="31"
                          v-model="currentInsurance.due_day"
                          required
                          :class="[
                            'w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:border-transparent',
                            formErrors.due_day 
                              ? 'border-red-500 focus:ring-red-500' 
                              : 'border-gray-200 dark:border-gray-700 focus:ring-blue-500'
                          ]"
                        />
                        <p v-if="formErrors.due_day" class="mt-1 text-sm text-red-600 dark:text-red-400">
                          {{ formErrors.due_day }}
                        </p>
                      </div>
                    </div>
                    
                    <div class="mt-4 space-y-3">
                      <div class="flex items-center">
                        <input
                          id="edit_mixed_value"
                          type="checkbox"
                          v-model="currentInsurance.edit_mixed_value"
                          class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label for="edit_mixed_value" class="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                          Editar valor misto
                        </label>
                      </div>
                      
                      <div class="flex items-center">
                        <input
                          id="round_account"
                          type="checkbox"
                          v-model="currentInsurance.round_account"
                          class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label for="round_account" class="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                          Arredondar conta
                        </label>
                      </div>
                      
                      <div class="flex items-center">
                        <input
                          id="show_service_value"
                          type="checkbox"
                          v-model="currentInsurance.show_service_value"
                          class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label for="show_service_value" class="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                          Mostrar valor serviço
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- ABA: Materiais -->
                <div v-if="activeTab === 'materials'" class="space-y-6">
                  <!-- Configurações de Materiais -->
                  <div>
                    <h3 class="text-lg font-medium mb-4 text-gray-900 dark:text-white">Configurações de Materiais</h3>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label for="enable_materials" class="block text-sm font-medium mb-1">Habilitar Materiais*</label>
                        <select
                          id="enable_materials"
                          v-model="currentInsurance.enable_materials"
                          required
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none pr-8"
                        >
                          <option value="">Selecione</option>
                          <option value="Sim">Sim</option>
                          <option value="Não">Não</option>
                        </select>
                      </div>
                      
                      <div>
                        <label for="materials_table" class="block text-sm font-medium mb-1">Tabela de Materiais*</label>
                        <select
                          id="materials_table"
                          v-model="currentInsurance.materials_table"
                          required
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none pr-8"
                        >
                          <option value="">Selecione</option>
                          <option value="CBHPM">CBHPM</option>
                          <option value="AMB">AMB</option>
                          <option value="Própria">Própria</option>
                        </select>
                      </div>
                      
                      <div>
                        <label for="automatic_material" class="block text-sm font-medium mb-1">Material Automático</label>
                        <select
                          id="automatic_material"
                          v-model="currentInsurance.automatic_material"
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none pr-8"
                        >
                          <option value="">Selecione</option>
                          <option value="Sim">Sim</option>
                          <option value="Não">Não</option>
                        </select>
                      </div>
                    </div>
                    
                    <div class="mt-4">
                      <label for="material_types" class="block text-sm font-medium mb-1">Tipos e Descrições de Material</label>
                      <textarea
                        id="material_types"
                        v-model="currentInsurance.material_types"
                        rows="3"
                        class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      ></textarea>
                    </div>
                    
                    <div class="mt-4">
                      <label for="authorized_kits" class="block text-sm font-medium mb-1">Kits Autorizados</label>
                      <textarea
                        id="authorized_kits"
                        v-model="currentInsurance.authorized_kits"
                        rows="3"
                        class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      ></textarea>
                    </div>
                  </div>
                </div>

                <!-- ABA: TISS -->
                <div v-if="activeTab === 'tiss'" class="space-y-6">
                  <!-- Configurações TISS -->
                  <div>
                    <h3 class="text-lg font-medium mb-4 text-gray-900 dark:text-white">Configurações TISS</h3>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label for="tiss_version" class="block text-sm font-medium mb-1">Versão TISS*</label>
                        <select
                          id="tiss_version"
                          v-model="currentInsurance.tiss_version"
                          required
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none pr-8"
                        >
                          <option value="">Selecione</option>
                          <option value="4.01.00">4.01.00</option>
                          <option value="3.04.00">3.04.00</option>
                        </select>
                      </div>
                      
                      <div>
                        <label for="tiss_origin" class="block text-sm font-medium mb-1">TISS Origem*</label>
                        <select
                          id="tiss_origin"
                          v-model="currentInsurance.tiss_origin"
                          required
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none pr-8"
                        >
                          <option value="">Selecione</option>
                          <option value="ANS">ANS</option>
                          <option value="CNPJ">CNPJ</option>
                        </select>
                      </div>
                      
                      <div>
                        <label for="grouping_type" class="block text-sm font-medium mb-1">Tipo Agrupamento*</label>
                        <select
                          id="grouping_type"
                          v-model="currentInsurance.grouping_type"
                          required
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none pr-8"
                        >
                          <option value="">Selecione</option>
                          <option value="Guia">Guia</option>
                          <option value="Guia e Data">Guia e Data</option>
                          <option value="Paciente">Paciente</option>
                        </select>
                      </div>
                      
                      <div>
                        <label for="contracted_type" class="block text-sm font-medium mb-1">Tipo Contratado*</label>
                        <select
                          id="contracted_type"
                          v-model="currentInsurance.contracted_type"
                          required
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none pr-8"
                        >
                          <option value="">Selecione</option>
                          <option value="Código Prestador">Código Prestador</option>
                          <option value="CNPJ">CNPJ</option>
                          <option value="CPF">CPF</option>
                        </select>
                      </div>
                      
                      <div>
                        <label for="send_as_operator_guide" class="block text-sm font-medium mb-1">Envio como Guia da Operadora*</label>
                        <select
                          id="send_as_operator_guide"
                          v-model="currentInsurance.send_as_operator_guide"
                          required
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none pr-8"
                        >
                          <option value="Sim">Sim</option>
                          <option value="Não">Não</option>
                        </select>
                      </div>
                      
                      <div>
                        <label for="empty_authorization" class="block text-sm font-medium mb-1">Autorização Vazia*</label>
                        <select
                          id="empty_authorization"
                          v-model="currentInsurance.empty_authorization"
                          required
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none pr-8"
                        >
                          <option value="">Selecione</option>
                          <option value="Sim">Sim</option>
                          <option value="Não">Não</option>
                        </select>
                      </div>
                      
                      <div>
                        <label for="crm_field" class="block text-sm font-medium mb-1">Preenchimento CRM*</label>
                        <select
                          id="crm_field"
                          v-model="currentInsurance.crm_field"
                          required
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none pr-8"
                        >
                          <option value="">Selecione</option>
                          <option value="Sim">Sim</option>
                          <option value="Não">Não</option>
                        </select>
                      </div>
                      
                      <div>
                        <label for="cnes_field" class="block text-sm font-medium mb-1">Preenchimento CNES*</label>
                        <select
                          id="cnes_field"
                          v-model="currentInsurance.cnes_field"
                          required
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none pr-8"
                        >
                          <option value="">Selecione</option>
                          <option value="Sim">Sim</option>
                          <option value="Não">Não</option>
                        </select>
                      </div>
                    </div>
                    
                    <div class="mt-4">
                      <label for="guide_files" class="block text-sm font-medium mb-1">Arquivos das Guias*</label>
                      <input
                        id="guide_files"
                        type="text"
                        v-model="currentInsurance.guide_files"
                        required
                        class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    
                    <div class="mt-4 space-y-3">
                      <div class="flex items-center">
                        <input
                          id="access_path"
                          type="checkbox"
                          v-model="currentInsurance.access_path"
                          class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label for="access_path" class="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                          Campo via de acesso
                        </label>
                      </div>
                      
                      <div class="flex items-center">
                        <input
                          id="show_procedure_value"
                          type="checkbox"
                          v-model="currentInsurance.show_procedure_value"
                          class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label for="show_procedure_value" class="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                          Exibir valor do procedimento
                        </label>
                      </div>
                      
                      <div class="flex items-center">
                        <input
                          id="sequential_tag"
                          type="checkbox"
                          v-model="currentInsurance.sequential_tag"
                          class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label for="sequential_tag" class="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                          Tag sequencial por paciente
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- ABA: BPA -->
                <div v-if="activeTab === 'bpa'" class="space-y-6">
                  <!-- Configurações BPA -->
                  <div>
                    <h3 class="text-lg font-medium mb-4 text-gray-900 dark:text-white">Configurações BPA</h3>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label for="destination_indicator" class="block text-sm font-medium mb-1">Indicador de Destino*</label>
                        <select
                          id="destination_indicator"
                          v-model="currentInsurance.destination_indicator"
                          required
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none pr-8"
                        >
                          <option value="">Selecione</option>
                          <option value="E">E</option>
                          <option value="M">M</option>
                        </select>
                      </div>
                      
                      <div>
                        <label for="use_clinic_zip" class="block text-sm font-medium mb-1">Usar CEP da Clínica*</label>
                        <select
                          id="use_clinic_zip"
                          v-model="currentInsurance.use_clinic_zip"
                          required
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none pr-8"
                        >
                          <option value="">Selecione</option>
                          <option value="Sim">Sim</option>
                          <option value="Não">Não</option>
                        </select>
                      </div>
                      
                      <div>
                        <label for="destination_organ" class="block text-sm font-medium mb-1">Órgão Destino</label>
                        <input
                          id="destination_organ"
                          type="text"
                          v-model="currentInsurance.destination_organ"
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <!-- ABA: NFSe -->
                <div v-if="activeTab === 'nfse'" class="space-y-6">
                  <!-- Configurações NFSe -->
                  <div>
                    <h3 class="text-lg font-medium mb-4 text-gray-900 dark:text-white">Configurações NFSe</h3>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label for="tax_incidence" class="block text-sm font-medium mb-1">Incidência de Impostos*</label>
                        <select
                          id="tax_incidence"
                          v-model="currentInsurance.tax_incidence"
                          required
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none pr-8"
                        >
                          <option value="">Selecione</option>
                          <option value="Lucro Real">Lucro Real</option>
                          <option value="Lucro Presumido">Lucro Presumido</option>
                          <option value="Simples Nacional">Simples Nacional</option>
                        </select>
                      </div>
                      
                      <div>
                        <label for="municipal_registration" class="block text-sm font-medium mb-1">Inscrição Municipal</label>
                        <input
                          id="municipal_registration"
                          type="text"
                          v-model="currentInsurance.municipal_registration"
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      
                      <div>
                        <label for="ir_rate" class="block text-sm font-medium mb-1">IR (%)</label>
                        <input
                          id="ir_rate"
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          v-model="currentInsurance.ir_rate"
                          :class="[
                            'w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:border-transparent',
                            formErrors.ir_rate 
                              ? 'border-red-500 focus:ring-red-500' 
                              : 'border-gray-200 dark:border-gray-700 focus:ring-blue-500'
                          ]"
                        />
                        <p v-if="formErrors.ir_rate" class="mt-1 text-sm text-red-600 dark:text-red-400">
                          {{ formErrors.ir_rate }}
                        </p>
                      </div>
                      
                      <div>
                        <label for="csll_rate" class="block text-sm font-medium mb-1">CSLL (%)</label>
                        <input
                          id="csll_rate"
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          v-model="currentInsurance.csll_rate"
                          :class="[
                            'w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:border-transparent',
                            formErrors.csll_rate 
                              ? 'border-red-500 focus:ring-red-500' 
                              : 'border-gray-200 dark:border-gray-700 focus:ring-blue-500'
                          ]"
                        />
                        <p v-if="formErrors.csll_rate" class="mt-1 text-sm text-red-600 dark:text-red-400">
                          {{ formErrors.csll_rate }}
                        </p>
                      </div>
                      
                      <div>
                        <label for="cofins_rate" class="block text-sm font-medium mb-1">COFINS (%)</label>
                        <input
                          id="cofins_rate"
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          v-model="currentInsurance.cofins_rate"
                          :class="[
                            'w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:border-transparent',
                            formErrors.cofins_rate 
                              ? 'border-red-500 focus:ring-red-500' 
                              : 'border-gray-200 dark:border-gray-700 focus:ring-blue-500'
                          ]"
                        />
                        <p v-if="formErrors.cofins_rate" class="mt-1 text-sm text-red-600 dark:text-red-400">
                          {{ formErrors.cofins_rate }}
                        </p>
                      </div>
                      
                      <div>
                        <label for="inss_rate" class="block text-sm font-medium mb-1">INSS (%)</label>
                        <input
                          id="inss_rate"
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          v-model="currentInsurance.inss_rate"
                          :class="[
                            'w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:border-transparent',
                            formErrors.inss_rate 
                              ? 'border-red-500 focus:ring-red-500' 
                              : 'border-gray-200 dark:border-gray-700 focus:ring-blue-500'
                          ]"
                        />
                        <p v-if="formErrors.inss_rate" class="mt-1 text-sm text-red-600 dark:text-red-400">
                          {{ formErrors.inss_rate }}
                        </p>
                      </div>
                      
                      <div>
                        <label for="pis_rate" class="block text-sm font-medium mb-1">PIS (%)</label>
                        <input
                          id="pis_rate"
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          v-model="currentInsurance.pis_rate"
                          :class="[
                            'w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:border-transparent',
                            formErrors.pis_rate 
                              ? 'border-red-500 focus:ring-red-500' 
                              : 'border-gray-200 dark:border-gray-700 focus:ring-blue-500'
                          ]"
                        />
                        <p v-if="formErrors.pis_rate" class="mt-1 text-sm text-red-600 dark:text-red-400">
                          {{ formErrors.pis_rate }}
                        </p>
                      </div>
                    </div>
                    
                    <div class="mt-4 space-y-3">
                      <div class="flex items-center">
                        <input
                          id="simples_nacional"
                          type="checkbox"
                          v-model="currentInsurance.simples_nacional"
                          class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label for="simples_nacional" class="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                          Simples Nacional
                        </label>
                      </div>
                      
                      <div class="flex items-center">
                        <input
                          id="retention_responsible"
                          type="checkbox"
                          v-model="currentInsurance.retention_responsible"
                          class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label for="retention_responsible" class="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                          Responsável pela retenção
                        </label>
                      </div>
                    </div>
                    
                    <!-- Campos obrigatórios NFSe -->
                    <div class="mt-6">
                      <h4 class="text-md font-medium mb-3 text-gray-900 dark:text-white">Configurações Obrigatórias NFSe</h4>
                      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label for="nfse_environment" class="block text-sm font-medium mb-1">Ambiente NFSe *</label>
                          <div class="relative">
                            <select
                              id="nfse_environment"
                              v-model="currentInsurance.nfse_environment"
                              required
                              :class="[
                                'w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none pr-8',
                                !currentInsurance.nfse_environment || currentInsurance.nfse_environment.trim() === '' ? 'border-red-300 dark:border-red-600' : 'border-gray-200 dark:border-gray-700'
                              ]"
                            >
                              <option value="">Selecione</option>
                              <option value="Producao">Produção</option>
                              <option value="Homologacao">Homologação</option>
                            </select>
                            <div v-if="!currentInsurance.nfse_environment || currentInsurance.nfse_environment.trim() === ''" 
                                 class="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 dark:text-red-400 pointer-events-none"
                                 title="Campo obrigatório">
                              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                              </svg>
                            </div>
                          </div>
                        </div>
                        
                        <div>
                          <label for="nfse_regime" class="block text-sm font-medium mb-1">Regime NFSe *</label>
                          <div class="relative">
                            <select
                              id="nfse_regime"
                              v-model="currentInsurance.nfse_regime"
                              required
                              :class="[
                                'w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none pr-8',
                                !currentInsurance.nfse_regime || currentInsurance.nfse_regime.trim() === '' ? 'border-red-300 dark:border-red-600' : 'border-gray-200 dark:border-gray-700'
                              ]"
                            >
                              <option value="">Selecione</option>
                              <option value="Lucro Real">Lucro Real</option>
                              <option value="Lucro Presumido">Lucro Presumido</option>
                              <option value="Simples Nacional">Simples Nacional</option>
                            </select>
                            <div v-if="!currentInsurance.nfse_regime || currentInsurance.nfse_regime.trim() === ''" 
                                 class="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 dark:text-red-400 pointer-events-none"
                                 title="Campo obrigatório">
                              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                              </svg>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- ABA: Autorizador -->
                <div v-if="activeTab === 'authorizer'" class="space-y-6">
                  <!-- Configurações do Autorizador -->
                  <div>
                    <h3 class="text-lg font-medium mb-4 text-gray-900 dark:text-white">Configurações do Autorizador</h3>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label for="request_authorization" class="block text-sm font-medium mb-1">Solicita Autorização*</label>
                        <select
                          id="request_authorization"
                          v-model="currentInsurance.request_authorization"
                          required
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none pr-8"
                        >
                          <option value="Sim">Sim</option>
                          <option value="Não">Não</option>
                        </select>
                      </div>
                      
                      <div>
                        <label for="cancel_guide" class="block text-sm font-medium mb-1">Cancelar Guia*</label>
                        <select
                          id="cancel_guide"
                          v-model="currentInsurance.cancel_guide"
                          required
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none pr-8"
                        >
                          <option value="">Selecione</option>
                          <option value="Sim">Sim</option>
                          <option value="Não">Não</option>
                        </select>
                      </div>
                      
                      <div>
                        <label for="authorization_status" class="block text-sm font-medium mb-1">Status Autorização*</label>
                        <select
                          id="authorization_status"
                          v-model="currentInsurance.authorization_status"
                          required
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none pr-8"
                        >
                          <option value="">Selecione</option>
                          <option value="Sim">Sim</option>
                          <option value="Não">Não</option>
                        </select>
                      </div>
                      
                      <div>
                        <label for="cnes_authorization" class="block text-sm font-medium mb-1">CNES para Autorização*</label>
                        <input
                          id="cnes_authorization"
                          type="text"
                          v-model="currentInsurance.cnes_authorization"
                          required
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      
                      <div>
                        <label for="company_code" class="block text-sm font-medium mb-1">Código Empresa Convênio</label>
                        <input
                          id="company_code"
                          type="text"
                          v-model="currentInsurance.company_code"
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      
                      <div>
                        <label for="guide_digit_number" class="block text-sm font-medium mb-1">Número Dígito Guia</label>
                        <input
                          id="guide_digit_number"
                          type="text"
                          v-model="currentInsurance.guide_digit_number"
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                    
                    <div class="mt-4 space-y-3">
                      <div class="flex items-center">
                        <input
                          id="automatic_guide_generation"
                          type="checkbox"
                          v-model="currentInsurance.automatic_guide_generation"
                          class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label for="automatic_guide_generation" class="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                          Geração de guia automática
                        </label>
                      </div>
                      
                      <div class="flex items-center">
                        <input
                          id="group_procedures"
                          type="checkbox"
                          v-model="currentInsurance.group_procedures"
                          class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label for="group_procedures" class="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                          Agrupar procedimentos
                        </label>
                      </div>
                      
                      <div class="flex items-center">
                        <input
                          id="mandatory_card_reading"
                          type="checkbox"
                          v-model="currentInsurance.mandatory_card_reading"
                          class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label for="mandatory_card_reading" class="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                          Leitura obrigatória
                        </label>
                      </div>
                      
                      <div class="flex items-center">
                        <input
                          id="eligibility_verification"
                          type="checkbox"
                          v-model="currentInsurance.eligibility_verification"
                          class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label for="eligibility_verification" class="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                          Verificação de elegibilidade
                        </label>
                      </div>
                    </div>
                    
                    <!-- Campos obrigatórios Autorizador -->
                    <div class="mt-6">
                      <h4 class="text-md font-medium mb-3 text-gray-900 dark:text-white">Configurações Obrigatórias do Autorizador</h4>
                      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label for="authorizer_type" class="block text-sm font-medium mb-1">Tipo de Autorizador *</label>
                          <div class="relative">
                            <select
                              id="authorizer_type"
                              v-model="currentInsurance.authorizer_type"
                              required
                              :class="[
                                'w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none pr-8',
                                !currentInsurance.authorizer_type || currentInsurance.authorizer_type.trim() === '' ? 'border-red-300 dark:border-red-600' : 'border-gray-200 dark:border-gray-700'
                              ]"
                            >
                              <option value="">Selecione</option>
                              <option value="TISS">TISS</option>
                              <option value="RIS">RIS</option>
                              <option value="Personalizado">Personalizado</option>
                            </select>
                            <div v-if="!currentInsurance.authorizer_type || currentInsurance.authorizer_type.trim() === ''" 
                                 class="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 dark:text-red-400 pointer-events-none"
                                 title="Campo obrigatório">
                              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                              </svg>
                            </div>
                          </div>
                        </div>
                        
                        <div>
                          <label for="authorizer_url" class="block text-sm font-medium mb-1">URL do Autorizador *</label>
                          <div class="relative">
                            <input
                              id="authorizer_url"
                              type="url"
                              v-model="currentInsurance.authorizer_url"
                              required
                              placeholder="https://exemplo.com/api"
                              :class="[
                                'w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                                !currentInsurance.authorizer_url || currentInsurance.authorizer_url.trim() === '' ? 'border-red-300 dark:border-red-600' : 'border-gray-200 dark:border-gray-700'
                              ]"
                            />
                            <div v-if="!currentInsurance.authorizer_url || currentInsurance.authorizer_url.trim() === ''" 
                                 class="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 dark:text-red-400"
                                 title="Campo obrigatório">
                              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                              </svg>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Form Actions -->
                <div class="mt-8 flex justify-between items-center">
                  <div class="flex gap-2">
                    <!-- Navigation buttons could go here -->
                  </div>
                  <div class="flex gap-3">
                    <button
                      class="px-4 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md transition-colors"
                      @click="goToList"
                    >
                      Cancelar
                    </button>
                    <button
                      class="clinic-btn text-white rounded-md transition-colors shadow-sm disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed"
                      @click="saveInsurance"
                      :disabled="!isFormValid || isSaving"
                    >
                      {{ isSaving ? 'Salvando...' : (editMode ? 'Atualizar' : 'Salvar') }} Convênio
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Success Modal -->
          <div
            class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            v-if="showSuccessModal"
            @click.self="closeSuccessModal"
          >
            <div
              class="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto animate-fadeIn"
            >
              <div class="p-6 text-center">
                <div
                  class="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center text-green-600 dark:text-green-400 mx-auto mb-4"
                >
                  <CheckIcon class="w-8 h-8" />
                </div>
                <h3 class="text-xl font-medium mb-2">{{ editMode ? 'Convênio Atualizado' : 'Convênio Cadastrado' }}</h3>
                <p class="text-gray-600 dark:text-gray-400 mb-6">
                  {{ editMode ? 'O convênio foi atualizado com sucesso.' : 'O convênio foi cadastrado com sucesso no sistema.' }}
                </p>
                <div class="flex justify-center gap-3">
                  <button
                    class="clinic-btn text-white rounded-md transition-colors shadow-sm"
                    @click="closeSuccessModal"
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Delete Confirmation Modal -->
          <div
            class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            v-if="showDeleteModal"
            @click.self="showDeleteModal = false"
          >
            <div
              class="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto animate-fadeIn"
            >
              <div class="p-6">
                <div
                  class="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center text-red-600 dark:text-red-400 mx-auto mb-4"
                >
                  <AlertTriangleIcon class="w-8 h-8" />
                </div>
                <h3 class="text-xl font-medium mb-2 text-center">Confirmar Exclusão</h3>
                <p class="text-gray-600 dark:text-gray-400 mb-6 text-center">
                  Tem certeza que deseja excluir o convênio <span class="font-medium">{{ insuranceToDelete?.name }}</span>?
                  <br />Esta ação não pode ser desfeita.
                </p>
                <div class="flex justify-end gap-3">
                  <button
                    class="px-4 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md transition-colors"
                    @click="showDeleteModal = false"
                  >
                    Cancelar
                  </button>
                  <button
                    class="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-md transition-colors shadow-sm"
                    @click="deleteInsurance"
                    :disabled="isDeleting"
                  >
                    {{ isDeleting ? 'Excluindo...' : 'Excluir' }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Modal de Vinculação de Convênio -->
          <div
            class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            v-if="showLinkConvenioModal"
            @click.self="closeLinkConvenioModal"
          >
            <div
              class="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-fadeIn"
            >
              <div class="p-6">
                <div class="flex justify-between items-center mb-4">
                  <h3 class="text-xl font-medium">Vincular Convênio de Faturamento</h3>
                  <button
                    @click="closeLinkConvenioModal"
                    class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <XIcon class="w-5 h-5" />
                  </button>
                </div>
                
                <div class="mb-4">
                  <p class="text-gray-600 dark:text-gray-400 mb-2">
                    Insurance: <span class="font-medium">{{ selectedInsurance?.name }}</span>
                  </p>
                  <p class="text-sm text-gray-500 dark:text-gray-400">
                    Selecione um convênio do módulo de Faturamento para vincular:
                  </p>
                </div>

                <!-- Loading -->
                <div v-if="isLoadingConvenios" class="py-8 flex justify-center">
                  <LoaderIcon class="w-8 h-8 animate-spin text-gray-500 dark:text-gray-400" />
                </div>

                <!-- Lista de Convênios -->
                <div v-else class="space-y-2 max-h-96 overflow-y-auto">
                  <div
                    v-for="convenio in conveniosDisponiveis"
                    :key="convenio.id"
                    class="p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                    :class="{
                      'bg-blue-50 dark:bg-blue-900/30 border-blue-300 dark:border-blue-600': selectedConvenioId === convenio.id
                    }"
                    @click="selectedConvenioId = convenio.id"
                  >
                    <div class="flex justify-between items-start">
                      <div class="flex-1">
                        <h4 class="font-medium text-gray-900 dark:text-gray-100">{{ convenio.nome }}</h4>
                        <p class="text-sm text-gray-600 dark:text-gray-400">Código: {{ convenio.codigo }}</p>
                        <p v-if="convenio.cnpj" class="text-sm text-gray-600 dark:text-gray-400">CNPJ: {{ formatCNPJDisplay(convenio.cnpj) }}</p>
                        <div class="flex gap-2 mt-2">
                          <span
                            v-if="convenio.tabela_nome"
                            class="px-2 py-1 text-xs bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 rounded"
                          >
                            Tabela: {{ convenio.tabela_nome }}
                          </span>
                          <span
                            v-if="convenio.tabela_material_nome"
                            class="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400 rounded"
                          >
                            Material: {{ convenio.tabela_material_nome }}
                          </span>
                        </div>
                      </div>
                      <div class="ml-4">
                        <span
                          v-if="convenio.ja_vinculado"
                          class="px-2 py-1 text-xs bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400 rounded"
                        >
                          Já vinculado
                        </span>
                        <div v-else class="w-4 h-4 border-2 border-gray-300 dark:border-gray-600 rounded-full flex items-center justify-center">
                          <div
                            v-if="selectedConvenioId === convenio.id"
                            class="w-2 h-2 bg-blue-600 dark:bg-blue-400 rounded-full"
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Error Message -->
                <div v-if="linkError" class="mt-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-md">
                  <p class="text-red-800 dark:text-red-400 text-sm">{{ linkError }}</p>
                </div>

                <!-- Actions -->
                <div class="flex justify-end gap-3 mt-6">
                  <button
                    class="px-4 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md transition-colors"
                    @click="closeLinkConvenioModal"
                  >
                    Cancelar
                  </button>
                  <button
                    class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors shadow-sm"
                    @click="linkConvenio"
                    :disabled="!selectedConvenioId || isLinking"
                  >
                    {{ isLinking ? 'Vinculando...' : 'Vincular' }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </template>
</BaseTemplate>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import BaseTemplate from '../templates/BaseTemplate.vue';
import apiClient from '@/services/authService';
import { useClinicStore } from '@/stores/settings/clinicStore';
import {
  validateCNPJ,
  validatePhone,
  validateCEP,
  validateEmail,
  validateANSRegistry,
  applyCNPJMask,
  applyPhoneMask,
  applyCEPMask,
  fetchAddressByCEP,
  formatCNPJ,
  
} from '@/utils/validations';
import {
  Plus as PlusIcon,
  ChevronDown as ChevronDownIcon,
  Check as CheckIcon,
  Edit as EditIcon,
  Trash as TrashIcon,
  Save as SaveIcon,
  ArrowLeft as ArrowLeftIcon,
  RefreshCw as RefreshCwIcon,
  Search as SearchIcon,
  Loader as LoaderIcon,
  X as XIcon,
  Link as LinkIcon,
  AlertTriangle as AlertTriangleIcon,
  FileText as FileTextIcon,
  Shield as ShieldIcon,
  CreditCard as CreditCardIcon,
  Settings as SettingsIcon,
  Database as DatabaseIcon,
  FileCheck as FileCheckIcon,
  Receipt as ReceiptIcon,
  Key as KeyIcon
} from 'lucide-vue-next';

// Inicializa o clinicStore (usado para aplicar estilos CSS automaticamente)
useClinicStore();

// Estado de exibição
const currentView = ref('list');
const editMode = ref(false);
const showSuccessModal = ref(false);
const showDeleteModal = ref(false);
const showLinkConvenioModal = ref(false);
const selectedInsurance = ref(null);
const conveniosDisponiveis = ref([]);
const selectedConvenioId = ref(null);
const isLoadingConvenios = ref(false);
const isLinking = ref(false);
const linkError = ref('');
const insuranceToDelete = ref(null);
const searchQuery = ref('');
const filterType = ref('');
const activeTab = ref('general');
const currentPage = ref(1);
const pageSize = ref(10);
const totalCount = ref(0);
const totalPages = ref(0);
const isLoading = ref(false);
const isSaving = ref(false);
const isDeleting = ref(false);
const errorMessage = ref('');
const formErrors = ref({});
const isSearchingCEP = ref(false);
const cepError = ref('');
const cnpjError = ref('');
const ansError = ref('');
const emailError = ref('');
const phoneError = ref('');
const cepValidationError = ref('');

// Debounce para busca
let searchTimeout = null;

// Abas do formulário
const tabs = [
  { id: 'general', name: 'Geral', icon: FileTextIcon },
  { id: 'validations', name: 'Validações', icon: ShieldIcon },
  { id: 'billing', name: 'Faturamento', icon: CreditCardIcon },
  { id: 'materials', name: 'Materiais', icon: DatabaseIcon },
  { id: 'tiss', name: 'TISS', icon: FileCheckIcon },
  { id: 'bpa', name: 'BPA', icon: SettingsIcon },
  { id: 'nfse', name: 'NFSe', icon: ReceiptIcon },
  { id: 'authorizer', name: 'Autorizador', icon: KeyIcon }
];

// Modelo vazio de convênio (ajustado para snake_case da API)
const emptyInsurance = {
  id: null,
  // Aba Geral
  name: '',
  cnpj: '',
  ans_registry: '',
  enabled: true,
  insurance_type: '',
  zip_code: '',
  state: '',
  city: '',
  street_type: '',
  street: '',
  number: '',
  complement: '',
  neighborhood: '',
  email1: '',
  email2: '',
  contact_name: '',
  phone1: '',
  phone2: '',
  phone3: '',
  observation: '',
  is_plan: false,
  
  // Aba Validações
  card_validation: 'Sim',
  provider_guide: 'Sim',
  guide_date: 'Sim',
  request_date_time: 'Sim',
  origin: 'Ambulatorial',
  cid: 'Sim',
  care_character: 'Eletivo',
  cns_patient: 'Sim',
  registration_definitions: '',
  doctor_code: '',
  validation_code: '',
  race: '',
  guide_logo: false,
  guide_numbering: false,
  scheduling_limit: false,
  
  // Aba Faturamento
  billing_type: 'Mensal',
  square_meter_value: 0.0,
  default_company: '',
  invoice_format: 'PDF',
  delivery_date: 1,
  bank_account: '',
  due_type: 'Fixo',
  due_day: 1,
  edit_mixed_value: false,
  round_account: false,
  show_service_value: false,
  
  // Aba Materiais
  enable_materials: 'Não',
  materials_table: '',
  automatic_material: '',
  material_types: '',
  authorized_kits: '',
  
  // Aba TISS
  tiss_version: '4.01.00',
  tiss_origin: 'ANS',
  grouping_type: 'Guia',
  contracted_type: 'Código Prestador',
  send_as_operator_guide: 'Sim',
  empty_authorization: 'Não',
  crm_field: 'Não',
  cnes_field: 'Não',
  guide_files: '',
  access_path: false,
  show_procedure_value: false,
  sequential_tag: false,
  
  // Aba BPA
  destination_indicator: 'E',
  use_clinic_zip: 'Sim',
  destination_organ: '',
  
  // Aba NFSe
  tax_incidence: 'Simples Nacional',
  municipal_registration: '',
  ir_rate: 0.0,
  csll_rate: 0.0,
  cofins_rate: 0.0,
  inss_rate: 0.0,
  pis_rate: 0.0,
  simples_nacional: false,
  retention_responsible: false,
  nfse_environment: 'Producao',
  nfse_regime: 'Simples Nacional',
  
  // Aba Autorizador
  request_authorization: 'Sim',
  cancel_guide: 'Não',
  authorization_status: 'Não',
  cnes_authorization: '',
  company_code: '',
  guide_digit_number: '',
  automatic_guide_generation: false,
  group_procedures: false,
  mandatory_card_reading: false,
  eligibility_verification: false,
  authorizer_type: 'TISS',
  authorizer_url: ''
};

// Dados reativos
const insurances = ref([]);
const currentInsurance = ref({ ...emptyInsurance });

// Computed properties
const isFormValid = computed(() => {
  const ins = currentInsurance.value;
  
  // Função auxiliar para verificar se uma string não está vazia
  const isValidString = (value) => value && typeof value === 'string' && value.trim() !== '';
  
  // VALIDAÇÃO SIMPLIFICADA: Apenas campos essenciais
  const essentialValidations = (
    isValidString(ins.name) &&           // Nome do convênio
    isValidString(ins.insurance_type)    // Tipo de convênio
  );
  
  // DEBUG: Log simplificado
  console.log('🔍 VALIDAÇÃO SIMPLIFICADA:', {
    name: ins.name,
    insurance_type: ins.insurance_type,
    essentialValidations,
    result: essentialValidations
  });
  
  return essentialValidations;
});

// Computed para listar o que falta ser preenchido
const missingFields = computed(() => {
  const ins = currentInsurance.value;
  const missing = [];
  
  // Função auxiliar para verificar se uma string não está vazia
  const isValidString = (value) => value && typeof value === 'string' && value.trim() !== '';
  
  // VERIFICAÇÃO SIMPLIFICADA: Apenas campos essenciais
  if (!isValidString(ins.name)) missing.push({ aba: 'Geral', campo: 'Nome do Convênio' });
  if (!isValidString(ins.insurance_type)) missing.push({ aba: 'Geral', campo: 'Tipo de Convênio' });
  
  return missing;
});

// Computed para mostrar mensagem de campos faltantes
const validationMessage = computed(() => {
  if (missingFields.value.length === 0) {
    return { type: 'success', message: 'Formulário Válido - Apenas Nome e Tipo são obrigatórios!' };
  }
  
  const missingList = missingFields.value.map(field => field.campo).join(', ');
  const message = `Campos obrigatórios: ${missingList}`;
  
  return { type: 'warning', message };
});

// Métodos de API
async function loadInsurances(page = 1) {
  isLoading.value = true;
  errorMessage.value = '';
  
  try {
    const params = {
      page,
      page_size: pageSize.value
    };
    
    if (searchQuery.value.trim()) {
      params.search = searchQuery.value.trim();
    }
    
    if (filterType.value) {
      params.insurance_type = filterType.value;
    }
    
    const response = await apiClient.get('/api/v1/insurances/list/', { params });
    
    insurances.value = response.data.results;
    totalCount.value = response.data.count;
    totalPages.value = response.data.total_pages;
    currentPage.value = page;
    
  } catch (error) {
    console.error('Erro ao carregar convênios:', error);
    errorMessage.value = 'Erro ao carregar convênios. Tente novamente.';
    
    if (error.response?.status === 401) {
      errorMessage.value = 'Sessão expirada. Faça login novamente.';
    }
  } finally {
    isLoading.value = false;
  }
}

async function saveInsurance() {
  if (!isFormValid.value || isSaving.value) return;
  
  console.log('🔍 DEBUG saveInsurance:', {
    isFormValid: isFormValid.value,
    isSaving: isSaving.value,
    editMode: editMode.value,
    currentInsurance: currentInsurance.value
  });
  
  isSaving.value = true;
  errorMessage.value = '';
  formErrors.value = {};
  
  console.log('🚀 Salvando convênio:', currentInsurance.value);
  
  try {
    // DEBUG: Log dos dados originais antes da limpeza
    console.log('🔍 DADOS ORIGINAIS (currentInsurance):', {
      tiss_version: currentInsurance.value.tiss_version,
      tiss_origin: currentInsurance.value.tiss_origin,
      billing_type: currentInsurance.value.billing_type,
      enable_materials: currentInsurance.value.enable_materials,
      automatic_material: currentInsurance.value.automatic_material,
      send_as_operator_guide: currentInsurance.value.send_as_operator_guide,
      empty_authorization: currentInsurance.value.empty_authorization,
      crm_field: currentInsurance.value.crm_field,
      cnes_field: currentInsurance.value.cnes_field,
      card_validation: currentInsurance.value.card_validation,
      provider_guide: currentInsurance.value.provider_guide,
      guide_date: currentInsurance.value.guide_date,
      request_date_time: currentInsurance.value.request_date_time,
      origin: currentInsurance.value.origin,
      cid: currentInsurance.value.cid,
      care_character: currentInsurance.value.care_character,
      cns_patient: currentInsurance.value.cns_patient
    });
    
    // Remove campos que não devem ser enviados
    const insuranceData = { ...currentInsurance.value };
    delete insuranceData.id;
    
    // Remove campos que podem ter valores inválidos
    const fieldsToRemove = [
      'convenio_faturamento',
      'convenio_faturamento_id', 
      'materials_table_id',
      'default_company_id',
      'bank_account_id',
      'created_at',
      'updated_at',
      'created_by',
      'updated_by'
    ];
    
    fieldsToRemove.forEach(field => {
      if (field in insuranceData) {
        delete insuranceData[field];
      }
    });
    
    // DEBUG: Log dos dados após limpeza
    console.log('🔍 DADOS APÓS LIMPEZA (insuranceData):', {
      tiss_version: insuranceData.tiss_version,
      tiss_origin: insuranceData.tiss_origin,
      billing_type: insuranceData.billing_type,
      enable_materials: insuranceData.enable_materials,
      automatic_material: insuranceData.automatic_material,
      send_as_operator_guide: insuranceData.send_as_operator_guide,
      empty_authorization: insuranceData.empty_authorization,
      crm_field: insuranceData.crm_field,
      cnes_field: insuranceData.cnes_field,
      card_validation: insuranceData.card_validation,
      provider_guide: insuranceData.provider_guide,
      guide_date: insuranceData.guide_date,
      request_date_time: insuranceData.request_date_time,
      origin: insuranceData.origin,
      cid: insuranceData.cid,
      care_character: insuranceData.care_character,
      cns_patient: insuranceData.cns_patient
    });
    
    // Converte campos "Sim"/"Não" para boolean
  const booleanFields = [
    'card_validation',
    'provider_guide', 
    'guide_date',
    'request_date_time',
    'cid',
    'care_character',
    'cns_patient',
    'guide_logo',
    'guide_numbering',
    'scheduling_limit',
    'edit_mixed_value',
    'round_account',
    'show_service_value',
    'access_path',
    'show_procedure_value',
    'sequential_tag',
    'simples_nacional',
    'retention_responsible',
    'automatic_guide_generation',
    'group_procedures',
    'mandatory_card_reading',
    'eligibility_verification',
    'enable_materials',
    'automatic_material',
    'send_as_operator_guide',
    'empty_authorization',
    'crm_field',
    'cnes_field',
    'use_clinic_zip',
    'request_authorization',
    'cancel_guide',
    'authorization_status'
  ];
    
    booleanFields.forEach(field => {
      if (insuranceData[field] === 'Sim') {
        insuranceData[field] = true;
      } else if (insuranceData[field] === 'Não') {
        insuranceData[field] = false;
      } else if (insuranceData[field] === '') {
        insuranceData[field] = null;
      }
    });
    
    // CORREÇÃO: Trata campos específicos que têm valores específicos
    if (insuranceData.origin === 'Ambulatorial') {
      insuranceData.origin = true;
    } else if (insuranceData.origin === 'Internação') {
      insuranceData.origin = false;
    }
    
    if (insuranceData.care_character === 'Eletivo') {
      insuranceData.care_character = true;
    } else if (insuranceData.care_character === 'Urgência') {
      insuranceData.care_character = false;
    }
    
    // CORREÇÃO: Campos que devem manter seus valores originais (não converter para boolean)
    // Estes campos já estão corretos da API e não precisam ser convertidos
    console.log('🔍 Campos preservados sem conversão:', {
      tiss_version: insuranceData.tiss_version,
      tiss_origin: insuranceData.tiss_origin,
      billing_type: insuranceData.billing_type,
      enable_materials: insuranceData.enable_materials,
      automatic_material: insuranceData.automatic_material,
      send_as_operator_guide: insuranceData.send_as_operator_guide,
      empty_authorization: insuranceData.empty_authorization,
      crm_field: insuranceData.crm_field,
      cnes_field: insuranceData.cnes_field,
      use_clinic_zip: insuranceData.use_clinic_zip,
      request_authorization: insuranceData.request_authorization,
      cancel_guide: insuranceData.cancel_guide,
      authorization_status: insuranceData.authorization_status,
      destination_indicator: insuranceData.destination_indicator,
      nfse_environment: insuranceData.nfse_environment,
      nfse_regime: insuranceData.nfse_regime,
      authorizer_type: insuranceData.authorizer_type
    });
    
    // CORREÇÃO: Trata campos numéricos que não podem ser null
    if (insuranceData.square_meter_value === null || insuranceData.square_meter_value === '' || insuranceData.square_meter_value === undefined) {
      insuranceData.square_meter_value = 0.0;
    }
    
    if (insuranceData.delivery_date === null || insuranceData.delivery_date === '' || insuranceData.delivery_date === undefined) {
      insuranceData.delivery_date = 1;
    }
    
    if (insuranceData.due_day === null || insuranceData.due_day === '' || insuranceData.due_day === undefined) {
      insuranceData.due_day = 1;
    }
    
    // CORREÇÃO: Trata campos de taxas que não podem ser null
    const taxFields = ['ir_rate', 'csll_rate', 'cofins_rate', 'inss_rate', 'pis_rate'];
    taxFields.forEach(field => {
      if (insuranceData[field] === null || insuranceData[field] === '' || insuranceData[field] === undefined) {
        insuranceData[field] = 0.0;
      }
    });
    
    // DEBUG: Log dos campos numéricos convertidos
    console.log('🔍 CAMPOS NUMÉRICOS CONVERTIDOS:', {
      square_meter_value: insuranceData.square_meter_value,
      delivery_date: insuranceData.delivery_date,
      due_day: insuranceData.due_day,
      ir_rate: insuranceData.ir_rate,
      csll_rate: insuranceData.csll_rate,
      cofins_rate: insuranceData.cofins_rate,
      inss_rate: insuranceData.inss_rate,
      pis_rate: insuranceData.pis_rate
    });
    
    // CORREÇÃO: Trunca campos que excedem o limite da API
    if (insuranceData.invoice_format && insuranceData.invoice_format.length > 3) {
      insuranceData.invoice_format = insuranceData.invoice_format.substring(0, 3);
    }
    
    if (insuranceData.tiss_origin && insuranceData.tiss_origin.length > 4) {
      insuranceData.tiss_origin = insuranceData.tiss_origin.substring(0, 4);
    }
    
    if (insuranceData.destination_indicator && insuranceData.destination_indicator.length > 1) {
      insuranceData.destination_indicator = insuranceData.destination_indicator.substring(0, 1);
    }
    
    if (insuranceData.nfse_environment && insuranceData.nfse_environment.length > 10) {
      insuranceData.nfse_environment = insuranceData.nfse_environment.substring(0, 10);
    }
    
    if (insuranceData.nfse_regime && insuranceData.nfse_regime.length > 15) {
      insuranceData.nfse_regime = insuranceData.nfse_regime.substring(0, 15);
    }
    
    if (insuranceData.authorizer_type && insuranceData.authorizer_type.length > 10) {
      insuranceData.authorizer_type = insuranceData.authorizer_type.substring(0, 10);
    }
    
               if (editMode.value) {
             console.log('🔄 Atualizando convênio ID:', currentInsurance.value.id);
             console.log('📤 Dados enviados:', insuranceData);
             console.log('🔍 Campos removidos:', {
               convenio_faturamento: currentInsurance.value.convenio_faturamento,
               convenio_faturamento_id: currentInsurance.value.convenio_faturamento_id,
               materials_table_id: currentInsurance.value.materials_table_id,
               default_company_id: currentInsurance.value.default_company_id,
               bank_account_id: currentInsurance.value.bank_account_id
             });
             
             // DEBUG: Log detalhado dos dados antes do envio
             console.log('🔍 DADOS DETALHADOS ENVIADOS:', {
               // Aba Geral
               name: insuranceData.name,
               cnpj: insuranceData.cnpj,
               ans_registry: insuranceData.ans_registry,
               insurance_type: insuranceData.insurance_type,
               zip_code: insuranceData.zip_code,
               state: insuranceData.state,
               city: insuranceData.city,
               street: insuranceData.street,
               number: insuranceData.number,
               complement: insuranceData.complement,
               neighborhood: insuranceData.neighborhood,
               email1: insuranceData.email1,
               email2: insuranceData.email2,
               contact_name: insuranceData.contact_name,
               phone1: insuranceData.phone1,
               phone2: insuranceData.phone2,
               phone3: insuranceData.phone3,
               observation: insuranceData.observation,
               is_plan: insuranceData.is_plan,
               enabled: insuranceData.enabled,
               
               // Aba Validações
               card_validation: insuranceData.card_validation,
               provider_guide: insuranceData.provider_guide,
               guide_date: insuranceData.guide_date,
               request_date_time: insuranceData.request_date_time,
               origin: insuranceData.origin,
               cid: insuranceData.cid,
               care_character: insuranceData.care_character,
               cns_patient: insuranceData.cns_patient,
               
               // Aba Faturamento
               billing_type: insuranceData.billing_type,
               square_meter_value: insuranceData.square_meter_value,
               delivery_date: insuranceData.delivery_date,
               due_day: insuranceData.due_day,
               due_type: insuranceData.due_type,
               enable_materials: insuranceData.enable_materials,
               automatic_material: insuranceData.automatic_material,
               send_as_operator_guide: insuranceData.send_as_operator_guide,
               empty_authorization: insuranceData.empty_authorization,
               crm_field: insuranceData.crm_field,
               cnes_field: insuranceData.cnes_field,
               
               // Aba TISS
               tiss_version: insuranceData.tiss_version,
               tiss_origin: insuranceData.tiss_origin,
               grouping_type: insuranceData.grouping_type,
               contracted_type: insuranceData.contracted_type,
               
               // Aba NFSe
               nfse_environment: insuranceData.nfse_environment,
               nfse_regime: insuranceData.nfse_regime,
               authorizer_type: insuranceData.authorizer_type,
               
               // Aba Autorizador
               request_authorization: insuranceData.request_authorization,
               cancel_guide: insuranceData.cancel_guide,
               authorization_status: insuranceData.authorization_status
             });
             
             const response = await apiClient.put(`/api/v1/insurances/update/${currentInsurance.value.id}/`, insuranceData);
             console.log('✅ Resposta da API:', response);
             
             // DEBUG: Comparar dados enviados vs retornados
             console.log('🔍 COMPARAÇÃO - DADOS ENVIADOS vs RETORNADOS:', {
               tiss_version_enviado: insuranceData.tiss_version,
               tiss_version_retornado: response.data?.tiss_version,
               tiss_origin_enviado: insuranceData.tiss_origin,
               tiss_origin_retornado: response.data?.tiss_origin,
               billing_type_enviado: insuranceData.billing_type,
               billing_type_retornado: response.data?.billing_type,
               enable_materials_enviado: insuranceData.enable_materials,
               enable_materials_retornado: response.data?.enable_materials
             });
             
             // DEBUG: Log completo da resposta da API
             console.log('🔍 RESPOSTA COMPLETA DA API:', response.data);
             
             // DEBUG: Verificar se todos os campos importantes estão na resposta
             const camposImportantes = [
               'tiss_version', 'tiss_origin', 'billing_type', 'enable_materials',
               'automatic_material', 'send_as_operator_guide', 'empty_authorization',
               'crm_field', 'cnes_field', 'card_validation', 'provider_guide',
               'guide_date', 'request_date_time', 'origin', 'cid', 'care_character',
               'cns_patient', 'square_meter_value', 'delivery_date', 'due_day',
               'ir_rate', 'csll_rate', 'cofins_rate', 'inss_rate', 'pis_rate'
             ];
             
             console.log('🔍 VERIFICAÇÃO DE CAMPOS IMPORTANTES:', camposImportantes.map(campo => ({
               campo,
               valor_enviado: insuranceData[campo],
               valor_retornado: response.data?.[campo],
               presente: campo in (response.data || {})
             })));
           } else {
             console.log('🆕 Criando novo convênio');
             console.log('📤 Dados enviados:', insuranceData);
             const response = await apiClient.post('/api/v1/insurances/add/', insuranceData);
             console.log('✅ Resposta da API:', response);
           }
    
    showSuccessModal.value = true;
    console.log('🎉 Convênio salvo com sucesso!');
    
    // Atualiza a lista
    await loadInsurances(currentPage.value);
    
  } catch (error) {
    console.error('❌ Erro ao salvar convênio:', error);
    console.error('❌ Detalhes do erro:', {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      config: error.config
    });
    
    if (error.response?.status === 401) {
      errorMessage.value = 'Sessão expirada. Faça login novamente.';
    } else if (error.response?.status === 400) {
      errorMessage.value = 'Dados inválidos. Verifique os campos obrigatórios.';
    } else {
      errorMessage.value = 'Erro ao salvar convênio. Tente novamente.';
    }
  } finally {
    isSaving.value = false;
    console.log('🏁 Finalizando salvamento, isSaving:', isSaving.value);
  }
}

async function deleteInsurance() {
  if (!insuranceToDelete.value || isDeleting.value) return;
  
  isDeleting.value = true;
  
  try {
    await apiClient.delete(`/api/v1/insurances/delete/${insuranceToDelete.value.id}/`);
    
    showDeleteModal.value = false;
    insuranceToDelete.value = null;
    
    // Atualiza a lista
    await loadInsurances(currentPage.value);
    
  } catch (error) {
    console.error('Erro ao excluir convênio:', error);
    
    if (error.response?.status === 401) {
      errorMessage.value = 'Sessão expirada. Faça login novamente.';
    } else {
      errorMessage.value = 'Erro ao excluir convênio. Tente novamente.';
    }
  } finally {
    isDeleting.value = false;
  }
}

// Métodos de controle da UI
function openNewInsuranceForm() {
  currentInsurance.value = JSON.parse(JSON.stringify(emptyInsurance));
  editMode.value = false;
  activeTab.value = 'general';
  currentView.value = 'form';
  errorMessage.value = '';
  formErrors.value = {};
  cepError.value = '';
}

function editInsurance(insurance) {
  console.log('✏️ Editando convênio:', insurance);
  
  // DEBUG: Log dos valores originais da API
  console.log('🔍 VALORES ORIGINAIS DA API:', {
    tiss_version: insurance.tiss_version,
    tiss_origin: insurance.tiss_origin,
    billing_type: insurance.billing_type,
    enable_materials: insurance.enable_materials,
    automatic_material: insurance.automatic_material,
    send_as_operator_guide: insurance.send_as_operator_guide,
    empty_authorization: insurance.empty_authorization,
    crm_field: insurance.crm_field,
    cnes_field: insurance.cnes_field,
    use_clinic_zip: insurance.use_clinic_zip,
    request_authorization: insurance.request_authorization,
    cancel_guide: insurance.cancel_guide,
    authorization_status: insurance.authorization_status,
    destination_indicator: insurance.destination_indicator,
    nfse_environment: insurance.nfse_environment,
    nfse_regime: insurance.nfse_regime,
    authorizer_type: insurance.authorizer_type
  });
  
  // Mescla os dados do convênio com o modelo vazio para garantir que todas as propriedades existam
  const insuranceData = { ...emptyInsurance, ...JSON.parse(JSON.stringify(insurance)) };
  
  // CORREÇÃO: Só converte campos boolean que realmente devem ser exibidos como "Sim"/"Não"
  // Campos como tiss_version, billing_type, etc. devem manter seus valores originais
  const booleanFieldsForDisplay = [
    'card_validation',
    'provider_guide', 
    'guide_date',
    'request_date_time',
    'cid',
    'care_character',
    'cns_patient',
    'guide_logo',
    'guide_numbering',
    'scheduling_limit',
    'edit_mixed_value',
    'round_account',
    'show_service_value',
    'access_path',
    'show_procedure_value',
    'sequential_tag',
    'simples_nacional',
    'retention_responsible',
    'automatic_guide_generation',
    'group_procedures',
    'mandatory_card_reading',
    'eligibility_verification'
  ];
  
  booleanFieldsForDisplay.forEach(field => {
    if (insuranceData[field] === true) {
      insuranceData[field] = 'Sim';
    } else if (insuranceData[field] === false) {
      insuranceData[field] = 'Não';
    } else if (insuranceData[field] === null || insuranceData[field] === undefined) {
      insuranceData[field] = '';
    }
  });
  
  // CORREÇÃO: Campos específicos que têm valores específicos da API
  if (insuranceData.origin === true) {
    insuranceData.origin = 'Ambulatorial';
  } else if (insuranceData.origin === false) {
    insuranceData.origin = 'Internação';
  }
  
  if (insuranceData.care_character === true) {
    insuranceData.care_character = 'Eletivo';
  } else if (insuranceData.care_character === false) {
    insuranceData.care_character = 'Urgência';
  }
  
  // CORREÇÃO: Campos que devem manter seus valores originais da API
  // Não converter estes campos para "Sim"/"Não"
  console.log('🔍 VALORES APÓS MESCLAGEM (preservados):', {
    tiss_version: insuranceData.tiss_version,
    tiss_origin: insuranceData.tiss_origin,
    billing_type: insuranceData.billing_type,
    enable_materials: insuranceData.enable_materials,
    automatic_material: insuranceData.automatic_material,
    send_as_operator_guide: insuranceData.send_as_operator_guide,
    empty_authorization: insuranceData.empty_authorization,
    crm_field: insuranceData.crm_field,
    cnes_field: insuranceData.cnes_field,
    use_clinic_zip: insuranceData.use_clinic_zip,
    request_authorization: insuranceData.request_authorization,
    cancel_guide: insuranceData.cancel_guide,
    authorization_status: insuranceData.authorization_status,
    destination_indicator: insuranceData.destination_indicator,
    nfse_environment: insuranceData.nfse_environment,
    nfse_regime: insuranceData.nfse_regime,
    authorizer_type: insuranceData.authorizer_type
  });
  
  currentInsurance.value = insuranceData;
  editMode.value = true;
  activeTab.value = 'general';
  currentView.value = 'form';
  errorMessage.value = '';
  formErrors.value = {};
  cepError.value = '';
  
  console.log('✅ Formulário de edição aberto:', {
    editMode: editMode.value,
    currentView: currentView.value,
    activeTab: activeTab.value,
    currentInsurance: currentInsurance.value
  });
}

function goToList() {
  console.log('🔙 Voltando para lista');
  currentView.value = 'list';
  errorMessage.value = '';
  formErrors.value = {};
  cepError.value = '';
}

function confirmDeleteInsurance(insurance) {
  insuranceToDelete.value = insurance;
  showDeleteModal.value = true;
}

function resetFilters() {
  searchQuery.value = '';
  filterType.value = '';
  loadInsurances(1);
}

function closeSuccessModal() {
  console.log('✅ Fechando modal de sucesso');
  showSuccessModal.value = false;
  currentView.value = 'list';
}

async function openLinkConvenioModal(insurance) {
  selectedInsurance.value = insurance;
  selectedConvenioId.value = null;
  linkError.value = '';
  showLinkConvenioModal.value = true;
  
  try {
    isLoadingConvenios.value = true;
    const response = await apiClient.get('/faturamento/convenios/para-vinculacao/');
    conveniosDisponiveis.value = response;
  } catch (error) {
    console.error('Erro ao carregar convênios:', error);
    linkError.value = 'Erro ao carregar convênios disponíveis.';
  } finally {
    isLoadingConvenios.value = false;
  }
}

function closeLinkConvenioModal() {
  showLinkConvenioModal.value = false;
  selectedInsurance.value = null;
  selectedConvenioId.value = null;
  conveniosDisponiveis.value = [];
  linkError.value = '';
}

async function linkConvenio() {
  if (!selectedConvenioId.value || !selectedInsurance.value) return;
  
  try {
    isLinking.value = true;
    linkError.value = '';
    
    await apiClient.post(`/insurances/${selectedInsurance.value.id}/link-convenio/`, {
      convenio_faturamento_id: selectedConvenioId.value
    });
    
    // Atualizar a lista de insurances
    await loadInsurances(currentPage.value);
    
    closeLinkConvenioModal();
    showSuccessModal.value = true;
  } catch (error) {
    console.error('Erro ao vincular convênio:', error);
    linkError.value = error.response?.data?.message || 'Erro ao vincular convênio.';
  } finally {
    isLinking.value = false;
  }
}

// Debounce para busca
function debouncedSearch() {
  if (searchTimeout) {
    clearTimeout(searchTimeout);
  }
  
  searchTimeout = setTimeout(() => {
    loadInsurances(1);
  }, 500);
}

// Utilitários
function formatCNPJDisplay(cnpj) {
  return formatCNPJ(cnpj);
}

// Watchers para aplicar máscaras e validações
watch(() => currentInsurance.value.cnpj, (newValue) => {
  if (newValue) {
    const maskedValue = applyCNPJMask(newValue);
    if (maskedValue !== newValue) {
      currentInsurance.value.cnpj = maskedValue;
    }
  }
});

watch(() => currentInsurance.value.phone1, (newValue) => {
  if (newValue) {
    const maskedValue = applyPhoneMask(newValue);
    if (maskedValue !== newValue) {
      currentInsurance.value.phone1 = maskedValue;
    }
  }
});

watch(() => currentInsurance.value.phone2, (newValue) => {
  if (newValue) {
    const maskedValue = applyPhoneMask(newValue);
    if (maskedValue !== newValue) {
      currentInsurance.value.phone2 = maskedValue;
    }
  }
});

watch(() => currentInsurance.value.phone3, (newValue) => {
  if (newValue) {
    const maskedValue = applyPhoneMask(newValue);
    if (maskedValue !== newValue) {
      currentInsurance.value.phone3 = maskedValue;
    }
  }
});

watch(() => currentInsurance.value.zip_code, (newValue) => {
  if (newValue) {
    const maskedValue = applyCEPMask(newValue);
    if (maskedValue !== newValue) {
      currentInsurance.value.zip_code = maskedValue;
    }
    
    // Busca endereço quando CEP estiver completo
    if (newValue.replace(/\D/g, '').length === 8) {
      searchAddressByCEP(newValue);
    }
  }
});

// Função para buscar endereço pelo CEP
async function searchAddressByCEP(cep) {
  if (!validateCEP(cep)) return;
  
  try {
    isSearchingCEP.value = true;
    cepError.value = '';
    
    const addressData = await fetchAddressByCEP(cep);
    
    // Preenche automaticamente os campos de endereço
    currentInsurance.value.state = addressData.uf;
    currentInsurance.value.city = addressData.localidade;
    currentInsurance.value.street = addressData.logradouro;
    currentInsurance.value.neighborhood = addressData.bairro;
    
    // Se não houver tipo de logradouro definido, tenta inferir
    if (!currentInsurance.value.street_type && addressData.logradouro) {
      const logradouro = addressData.logradouro.toLowerCase();
      if (logradouro.includes('rua')) {
        currentInsurance.value.street_type = 'Rua';
      } else if (logradouro.includes('avenida') || logradouro.includes('av.')) {
        currentInsurance.value.street_type = 'Avenida';
      } else if (logradouro.includes('alameda')) {
        currentInsurance.value.street_type = 'Alameda';
      } else if (logradouro.includes('praça')) {
        currentInsurance.value.street_type = 'Praça';
      }
    }
    
  } catch (error) {
    console.error('Erro ao buscar CEP:', error);
    cepError.value = error.message;
  } finally {
    isSearchingCEP.value = false;
  }
}

// Lifecycle
onMounted(async () => {
  await loadInsurances(1);
  currentView.value = 'list';
});

// Helper function to get missing fields for a specific tab
function getTabMissingFields(tabId) {
  const tabMapping = {
    'general': 'Geral',
    'validations': 'Validações',
    'billing': 'Faturamento',
    'materials': 'Materiais',
    'tiss': 'TISS',
    'bpa': 'BPA',
    'nfse': 'NFSe',
    'authorizer': 'Autorizador'
  };
  
  const tabName = tabMapping[tabId];
  return missingFields.value.filter(field => field.aba === tabName);
}

function formatCNPJInput(event) {
  let value = event.target.value.replace(/\D/g, '');
  
  // Aplicar máscara do CNPJ
  if (value.length <= 2) {
    // value já está correto, não precisa de atribuição
  } else if (value.length <= 5) {
    value = value.slice(0, 2) + '.' + value.slice(2);
  } else if (value.length <= 8) {
    value = value.slice(0, 2) + '.' + value.slice(2, 5) + '.' + value.slice(5);
  } else if (value.length <= 12) {
    value = value.slice(0, 2) + '.' + value.slice(2, 5) + '.' + value.slice(5, 8) + '/' + value.slice(8);
  } else {
    value = value.slice(0, 2) + '.' + value.slice(2, 5) + '.' + value.slice(5, 8) + '/' + value.slice(8, 12) + '-' + value.slice(12, 14);
  }
  
  currentInsurance.value.cnpj = value;
}

function validateCNPJField() {
  cnpjError.value = validateCNPJ(currentInsurance.value.cnpj) ? '' : 'CNPJ inválido';
}

function validateEmailField() {
  emailError.value = validateEmail(currentInsurance.value.email1) ? '' : 'E-mail inválido';
}

function validatePhoneField() {
  phoneError.value = validatePhone(currentInsurance.value.phone1) ? '' : 'Telefone inválido';
}

function formatCEPInput(event) {
  let value = event.target.value.replace(/\D/g, '');
  
  // Aplicar máscara do CEP
  if (value.length <= 5) {
    // value já está correto, não precisa de atribuição
  } else {
    value = value.slice(0, 5) + '-' + value.slice(5, 8);
  }
  
  currentInsurance.value.zip_code = value;
}

function validateCEPField() {
  cepValidationError.value = validateCEP(currentInsurance.value.zip_code) ? '' : 'CEP inválido';
}

function formatANSInput(event) {
  currentInsurance.value.ans_registry = event.target.value.replace(/\D/g, '');
}

function validateANSField() {
  ansError.value = validateANSRegistry(currentInsurance.value.ans_registry) ? '' : 'Registro ANS inválido';
}

function formatPhoneInput(event) {
  let value = event.target.value.replace(/\D/g, '');
  
  // Aplicar máscara do telefone
  if (value.length <= 2) {
    value = '(' + value;
  } else if (value.length <= 6) {
    value = '(' + value.slice(0, 2) + ') ' + value.slice(2);
  } else if (value.length <= 10) {
    value = '(' + value.slice(0, 2) + ') ' + value.slice(2, 6) + '-' + value.slice(6);
  } else {
    value = '(' + value.slice(0, 2) + ') ' + value.slice(2, 7) + '-' + value.slice(7);
  }
  
  currentInsurance.value.phone1 = value;
}
</script>

<style>
@import 'tailwindcss/base';
@import 'tailwindcss/components';
@import 'tailwindcss/utilities';

.clinic-btn {
  background-color: var(--clinic-btn-bg);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-weight: 500;
  transition: all 0.2s ease-in-out;
  border: none;
  cursor: pointer;
}

.clinic-btn:hover:not(:disabled) {
  background-color: var(--clinic-btn-bg-hover);
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.clinic-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.input-font-color {
  color: var(--clinic-font-color);
}

.animate-fadeIn {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>